"use client";

import { motion } from "framer-motion";
import {
  MessageCircleOff,
  PackageX,
  CreditCard,
  Layers,
  MessageCirclePlus,
  PackageCheck,
  ShieldCheck,
  LayoutDashboard,
  AlertTriangle,
  TrendingDown,
  CheckCircle2,
  TrendingUp,
} from "lucide-react";

const painCards = [
  {
    icon: MessageCircleOff,
    title: "Cliente xingando no Instagram",
    desc: "Mensagem sem resposta há 3 horas. Reputação caindo. Sem histórico do pedido na mão.",
    color: "text-red-500",
    bg: "bg-red-500/10",
    border: "border-red-500/20",
    isBad: true,
  },
  {
    icon: PackageX,
    title: "Rastreio sumiu",
    desc: "Pedido parado há 5 dias. Ninguém sabe onde está. Cliente ameaça chargeback.",
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    isBad: true,
  },
  {
    icon: CreditCard,
    title: "Chargeback subindo",
    desc: "Reembolsos mês a mês sem entender o motivo. Margem sangrando sem dados para agir.",
    color: "text-red-600",
    bg: "bg-red-600/10",
    border: "border-red-600/20",
    isBad: true,
  },
  {
    icon: Layers,
    title: "5 abas abertas todo dia",
    desc: "Shopify, WhatsApp Web, Excel, transportadora, Instagram. Sua equipe está cega e exausta.",
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    isBad: true,
  },
];

const solutionCards = [
  {
    icon: MessageCirclePlus,
    title: "Histórico do pedido no atendimento",
    desc: "Cada mensagem no WhatsApp ou Instagram traz automaticamente o pedido, rastreio e histórico do cliente.",
    color: "text-[#48c1ce]",
    bg: "bg-[#48c1ce]/10",
    border: "border-[#48c1ce]/20",
  },
  {
    icon: PackageCheck,
    title: "Alerta antes do prazo vencer",
    desc: "Mulply monitora o SLA e avisa sua equipe quando um envio está atrasando — antes do cliente reclamar.",
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
  {
    icon: ShieldCheck,
    title: "Mapa de motivos de reembolso",
    desc: "Dashboards de causas de chargeback em tempo real. Aja na causa — não no sintoma.",
    color: "text-[#ec399a]",
    bg: "bg-[#ec399a]/10",
    border: "border-[#ec399a]/20",
  },
  {
    icon: LayoutDashboard,
    title: "Uma tela. Tudo visível.",
    desc: "Logística, atendimento e CRM centralizados. Sua equipe age mais rápido com menos esforço.",
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
];

function BentoCard({
  icon: Icon,
  title,
  desc,
  color,
  bg,
  border,
  isBad,
  delay,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  color: string;
  bg: string;
  border: string;
  isBad?: boolean;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className={`relative p-5 rounded-2xl border ${border} ${bg} backdrop-blur-sm group hover:-translate-y-1 transition-all duration-300`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${bg} border ${border} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {isBad ? (
              <TrendingDown className="w-3 h-3 text-red-400" />
            ) : (
              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            )}
            <h3 className="text-sm font-black text-gray-800 dark:text-gray-100 leading-tight">{title}</h3>
          </div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function PainBentoLP3() {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-300/30 bg-red-500/10 text-red-500 text-xs font-bold mb-4">
            <AlertTriangle className="w-3 h-3" />
            A Realidade do Pós-Venda Sem o Mulply
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 dark:text-white">
            Escalar quebrou seu
            <br />
            <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
              atendimento.
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Cada pedido a mais é um problema a mais. Até que você centraliza tudo.
          </p>
        </motion.div>

        {/* Two-column Bento */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Left: Pain */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-xs font-black uppercase tracking-widest text-red-500">Sem o Mulply</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {painCards.map((card, i) => (
                <BentoCard key={card.title} {...card} delay={i * 0.08} />
              ))}
            </div>
          </div>

          {/* Right: Solution */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#48c1ce]" />
              <span className="text-xs font-black uppercase tracking-widest text-[#48c1ce]">Com o Mulply</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {solutionCards.map((card, i) => (
                <BentoCard key={card.title} {...card} delay={i * 0.08 + 0.1} />
              ))}
            </div>
          </div>
        </div>

        {/* Center divider stat */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 flex items-center justify-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-[#48c1ce]/10 to-[#ec399a]/10 border border-[#48c1ce]/20">
            <TrendingUp className="w-5 h-5 text-[#48c1ce]" />
            <p className="text-sm font-bold text-gray-700 dark:text-gray-200">
              Lojas no Mulply reduzem chargebacks em{" "}
              <span className="text-[#48c1ce]">até 43%</span> no primeiro trimestre
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
